import React, {useState} from "react";
import LinePrimaryChart from "../LinePrimaryChart/index"
import {LazyLog} from 'react-lazylog';
import "./index.css";
import useAxios from "axios-hooks";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {Grid, Snackbar, SnackbarCloseReason} from "@material-ui/core";
import {Skeleton, Alert} from "@material-ui/lab";
import {DataPriceProps, DataValueProps} from "../../interfaces/DataProps";
import {WaveletProps} from "./interfaces";

export const Main: React.FC<WaveletProps> = ({series}) => {

    const seriesMap = new Map<string, string>([
        ["CPI", "CPIAUCSL"],
        ["GDP", "GDP"],
        ["Money-Supply", "WM2NS"]
    ]);

    const gridItemRef = React.useRef<HTMLDivElement>(null);

    const {height} = useWindowDimensions();

    const [loading, setLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [boxWidth, setBoxWidth] = React.useState<number>(0);
    const [isErrorMessage, setIsErrorMessage] = React.useState<string>("");

    let runlog = null
    let path = null

    path = "correlations/output/wavelets"
    runlog = <LazyLog url={path + "/" + "run.log"}/>

    const [{data: data_btc, loading: loading_btc, error: error_btc}, fetch_btc] = useAxios(
        {
            url: `correlations/data/coingecko/BTCUSD/marketdata.csv`,
            method: "GET",
        },
        {manual: true}
    );

    // https://levelup.gitconnected.com/read-csv-file-in-node-js-and-typescript-91cf98e2e64e
    function csvToArr(stringVal: any, splitter: any) {
        const [keys, ...rest] = stringVal
            .trim()
            .split("\n")
            .map((item: string) => item.split(splitter));

        const formedArr = rest.map((item: string | any[]) => {
            const object = {} as any;
            keys.forEach((key: any, index: any) => (object[key] = item.at(index)));
            return object;
        });
        return formedArr;
    }

    function formatDate(date: any) {
        return (date.getFullYear() + "/" + (date.getMonth() + 1) + '/' + date.getDate())
    }

    const mappedBtcChartData: DataPriceProps[] = React.useMemo(() => {
        return data_btc
            ? csvToArr(data_btc, ",").map((ele: any) => ({
                date: formatDate(new Date(Number(ele.date))),
                price: Number(ele.price),
            }))
            : [];
    }, [data_btc]);

    const [{data: data_cpi, loading: loading_cpi, error: error_cpi}, fetch_cpi] = useAxios(
        {
            url: 'correlations/data/fred/' + seriesMap.get(series) +  '.csv',
            method: "GET",
        },
        {manual: true}
    );

    const mappedCpiChartData: DataValueProps[] = React.useMemo(() => {
        return data_cpi
            ? csvToArr(data_cpi, ",").map((ele: any) => ({
                date: formatDate(new Date(Number(ele.date))),
                value: Number(ele.value),
            }))
            : [];
    }, [data_cpi]);

    React.useEffect(() => {
        fetch_btc();
        fetch_cpi();
    }, [fetch_btc, fetch_cpi]);

    React.useEffect(() => {
        const handleResize = (width?: number) => {
            setBoxWidth(width || 0);
        };

        handleResize(gridItemRef.current?.clientWidth || 0);

        window.addEventListener("resize", () =>
            handleResize(gridItemRef?.current?.clientWidth || 0)
        );

        return () => {
            window.removeEventListener("resize", () => handleResize());
        };
    }, [gridItemRef]);

    const handleError = (
        e: React.SyntheticEvent<any>,
        reason?: SnackbarCloseReason
    ) => {
        setIsErrorMessage("");
    };

    return (
        <Grid container justify="center">
            <Grid ref={gridItemRef} item xs={12} md={10} lg={8}>
                {(loading_cpi) ? (
                    <Skeleton
                        variant="rect"
                        height={600}
                        width={800}
                    />
                ) : mappedCpiChartData?.length ? (
                    <>
                        <span>BTC vs {series}</span>
                        <LinePrimaryChart
                            data={mappedBtcChartData ?? []}
                            data_secondary={mappedCpiChartData ?? []}
                            height={Math.floor(height * 0.4)}
                            width={boxWidth}
                            margin={{
                                top: 16,
                                right: 56,
                                bottom: 40,
                                left: 100,
                            }}/>
                    </>
                ) : null}
                <img src={path + "/" + seriesMap.get(series) + "/" + "bitcoin.png"}/>
                <img src={path + "/" + seriesMap.get(series) + "/" + seriesMap.get(series)  + ".png"}/>
                <img src={path + "/" + seriesMap.get(series) + "/" + "wavelet-coherence.png"}/>
                <div>
                    <div>References</div>
                    <br />
                    <a href="https://fred.stlouisfed.org">Federal Reserve Economic Data | FRED | St. Louis Fed</a>
                    <br />
                    <a href="https://atoc.colorado.edu/research/wavelets">A Practical Guide to Wavelet Analysis</a>
                    <br />
                    <a href="https://pycwt.readthedocs.io/en/latest/#">PyCWT: spectral analysis using wavelets in Python</a>
                    <br />
                    <a href="https://github.com/Akshaay1/Financial-Time-Series-Analysis-using-Wavelets">Financial Time Series Analysis using Wavelets</a>
                    <br />
                </div>
                <br />
                <br />
                <div className="runlog">
                    <span>Run log</span>
                    {loading ? <div>Loading...</div> : (hasError ? <div>Error occured.</div> :
                        runlog)
                    }
                </div>
            </Grid>
            <Snackbar open={!!isErrorMessage} onClose={handleError}>
                <Alert onClose={handleError} severity="error">
                    {isErrorMessage}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default Main;
